'use client';
import MuiBadge from '@mui/material/Badge';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { boxShadowBadge } from '../../shadows';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import Button from '../Button';
import IconButton from '../IconButton';
import IconMenuButton from '../IconMenuButton';

export const styles = {
  badge: {
    top: -15,
    right: -12,
  },
  dot: {
    top: -10,
    right: -8,
  },
  flippedBadgeRoot: {
    '&.MuiButtonBase-root': {
      position: 'relative',
    },
    '&.MuiButton-root .MuiBadge-root': {
      position: 'static',
    },
  },
};

const useStyles = makeStyles()(styles);

export const Badge = ({
  children,
  classes: classesProp,
  forwardedRef: ref,
  backgroundColor = 'accent',
  borderColor = 'default',
  fontColor = 'defaultInverse',
  variant,
  badgeContent,
  sx,
  ...rest
}) => {
  const { classes, cx, theme } = useStyles();
  const idsTokens = theme.idsTokens ?? idsTokensDefault;
  const shouldFlip = [Button, IconButton, IconMenuButton].includes(children?.type);

  // Determine the background color dynamically
  const resolvedBackgroundColor = backgroundColor.includes('Inverse')
    ? idsTokens.badge.default.color.background.inverse[
        backgroundColor.replace('Inverse', '').toLowerCase()
      ]
    : idsTokens.badge.default.color.background.default[backgroundColor];

  const inner = (
    <MuiBadge
      classes={{
        ...((children?.type?.muiName === 'SvgIcon' || children?.type === IconButton) && classes),
        ...classesProp,
      }}
      overlap="rectangular"
      variant={variant}
      badgeContent={badgeContent}
      ref={shouldFlip ? null : ref}
      sx={[
        {
          '& .MuiBadge-badge': {
            backgroundColor: resolvedBackgroundColor,
            color: idsTokens.badge.default.color.label[fontColor],
            border: '1px solid',
            borderColor: idsTokens.badge.default.color.border[borderColor],
            boxShadow: boxShadowBadge,
            // Add conditional styling for single-character content in standard badges only
            ...(variant !== 'dot' && badgeContent?.toString().length === 1
              ? {
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%', // Makes it circular
                }
              : {}),
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {shouldFlip ? children.props.children : children}
    </MuiBadge>
  );
  return shouldFlip ? (
    <children.type
      {...children.props}
      ref={ref}
      className={cx(children.props.className, classes.flippedBadgeRoot)}
    >
      {inner}
    </children.type>
  ) : (
    <>{inner}</>
  );
};

Badge.propTypes = {
  /** The background color used for the Badge. */
  backgroundColor: PropTypes.oneOf([
    'accent',
    'brand',
    'decorative1',
    'decorative2',
    'decorative3',
    'decorative4',
    'decorative5',
    'neutral',
    'accentInverse',
    'brandInverse',
    'decorative1Inverse',
    'decorative2Inverse',
    'decorative3Inverse',
    'decorative4Inverse',
    'decorative5Inverse',
    'neutralInverse',
  ]),
  /** Content rendered within the badge. */
  badgeContent: PropTypes.node,
  /** The border color used for the Badge. */
  borderColor: PropTypes.oneOf(['default', 'defaultInverse']),
  /** The badge is added relative to this node. */
  children: PropTypes.node,
  /** Override or extend the styles applied to the component. */
  classes: PropTypes.object,
  /** The font color used for the Badge. */
  fontColor: PropTypes.oneOf(['default', 'defaultInverse']),
  /** If `true`, the badge will be invisible. */
  invisible: PropTypes.bool,
  /** Maximum count to show. */
  max: PropTypes.number,
  /** If `true`, the badge is still shown when badgeContent is zero. */
  showZero: PropTypes.bool,
  /** The variant to use. */
  variant: PropTypes.oneOf(['standard', 'dot']),
};

export default withRef()(Badge);
